/* eslint-disable react-hooks/exhaustive-deps */
import { DeviceType, InstallationTemplate } from '@eagle/core-data-types';
import { Button, Card, CardContent, CardHeader, Collapse, Divider, Pagination, Stack, styled, Tab } from '@ethings/ui-toolkit';
import { TabContext, TabPanel } from '@mui/lab';
import { Box, Grid, PaginationItem, Tabs, tabsClasses, useTheme } from '@mui/material';
import { FC, Fragment, useEffect, useMemo, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { T_MANY, T_ONE } from '../../constants';
import { useSmallScreen } from '../../hooks';
import { useSearch } from '../../pages/list/use-search';
import { CacheDataTypes, Nullable } from '../../types';
import { FILTER_OUT, getListResultDescription, testid } from '../../util';
import { AssignDialog } from '../assign-dialog';
import { DeleteEntityDialog } from '../delete-entity-dialog';
import { FetchOneOfAll } from '../fetch';
import { useBoolFlag } from '../flags';
import { Icon } from '../icons';
import { ListPaperColumn } from '../list-item';
import { LoadingButton } from '../loading-button';
import { MiddleSpinner } from '../middle-spinner';
import { ResponsiveText } from '../responsive-text';
import { SimpleElement, simpleRenderer } from '../simple-element';
import { EntityManagementCardProps, EntityTypeBase } from './controller';

const StyledCard = styled(Card)({
  width: '100%',
});

const StyledCardContent = styled(CardContent)({
  maxWidth: '42rem',
  padding: 0,
});

const StyledCardHeader = styled(CardHeader)({
  '.MuiCardHeader-action': { flex: 'auto' },
});

export const View: FC<EntityManagementCardProps> = ({
  assignErrorToast,
  account,
  addShareDialog,
  apiData,
  apiUri,
  assignEntityType,
  data,
  deleteState,
  disableAddShare = false,
  entityData,
  handleChange,
  index,
  isLoading: isDataLoading,
  onAddShare,
  removeEntity,
  renderRow,
  secondaryText,
  setAddShareDialog,
  setDeleteState,
  title,
  ...props
}) => {
  const { t } = useTranslation(['common']);
  const buttonVisible = useBoolFlag('manage-account-detail-add-shared-entity');
  const { result, pagination, isLoading, setPagination } = useSearch();
  const [idsToCheck, setIdsToCheck] = useState<string[]>([]);
  const smallScreen = useSmallScreen();
  const isSafari = navigator.userAgent.indexOf('Safari') !== -1;
  const listPageScroll = document.getElementById('list-page-scroll') as Nullable<HTMLDivElement>;
  const theme = useTheme();
  const dialogEntityType = addShareDialog.entityType ?? '';

  const matchCount = result?.matchCount ?? 0;

  const titleText = title ?? t('common:common.labels.entity-management');

  const paginationComponent = useMemo(
    () => matchCount > pagination.limit
      ? <Pagination
        count={Math.ceil(matchCount / pagination.limit)}
        disabled={isLoading || !!isDataLoading}
        onChange={(_, page) => {
          setPagination({ limit: pagination.limit, skip: pagination.limit * (page - 1) });
          if (!listPageScroll) return;
          if (isSafari) listPageScroll.scrollIntoView();
          else listPageScroll.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }}
        renderItem={(item) => (
          <PaginationItem data-testid={testid`pagination-item-${item.type}${item.type === 'page' ? `-${item.page ?? ''}` : ''}`}
            {...item}
          />
        )}
        page={Math.floor(pagination.skip / pagination.limit) + 1}
        showFirstButton={!smallScreen}
        showLastButton={!smallScreen}
        size={smallScreen ? 'small' : 'large'}
      />
      : undefined,
    [isLoading, isDataLoading, apiData?.hasMore, matchCount, pagination, smallScreen],
  );

  const openRemoveDialog = (item: EntityTypeBase, type: CacheDataTypes): void => {
    setDeleteState({ open: true, item, type, loading: false });
  };

  useEffect(() => {
    if (!entityData) return;
    setIdsToCheck(entityData.map(({ _id }) => _id));
  }, [entityData]);

  const renderSingleOrMultiple = data.length <= 1
    ? <Box>
      <ResponsiveText
        color="text.secondary"
        responsiveOptions={{
          minFontSize: '0.75rem',
          maxFontSize: '0.875rem',
          textLengthThreshold: 40,
        }}
        style={{ paddingLeft: '24px', paddingRight: '24px' }}
      >
        {secondaryText}
      </ResponsiveText>
      {data.map(({ entityType, entityKey }) => (
        !apiData?.items.length
          ? t('common:component.entity-management.hint.no-shared-entity', { entity: t(entityKey, { count: T_MANY }) })
          : apiData.items.map((item, itemIndex) => <Fragment key={item.display}>{renderRow(item, entityType, itemIndex, isLoading, openRemoveDialog)}<Divider /></Fragment>)
      ))
      }
    </Box>
    : <TabContext value={String(index)}>
      <Tabs
        allowScrollButtonsMobile
        onChange={handleChange}
        scrollButtons="auto"
        sx={{
          [`& .${tabsClasses.scrollButtons}`]: {
            '&.Mui-disabled': { display: 'none' },
          },
          '& .MuiTabs-flexContainer': {
            justifyContent: 'space-between',
          },
          borderBottom: 1,
          borderColor: 'divider',
        }}
        value={index}
        variant="scrollable"
      >
        {data.map(({ entityType, entityKey }, i) => <Tab data-testid={`tab-${i}`} key={`${entityType}-${i}`} label={t(entityKey, { count: T_MANY })} value={i} />)}
      </Tabs>
      {data.map(({ entityType, entityKey }, i) => <TabPanel
        key={`${entityType}-${i}`}
        sx={{
          p: !apiData?.items.length ? '2rem 1rem' : 0,
        }}
        value={String(i)}
      >
        {isLoading || isDataLoading
          ? <MiddleSpinner sx={{ padding: 2 }} />
          : !apiData?.items.length
            ? t('common:component.entity-management.hint.no-shared-entity', { entity: t(entityKey, { count: T_MANY }) })
            : (
              <Stack pt={2} spacing={1}>
                <ResponsiveText
                  color="text.secondary"
                  variant='subtitle2'
                  responsiveOptions={{
                    minFontSize: '0.75rem',
                    maxFontSize: '0.875rem',
                    textLengthThreshold: 40,
                  }}
                  style={{ paddingLeft: '24px', paddingRight: '24px', fontStyle: 'italic' }}
                >
                  {getListResultDescription({ count: apiData.count, entityKey, t })}
                </ResponsiveText>
                <div>
                  {apiData.items.map((item, apiIndex) => <Fragment key={`${item.display}-${apiIndex}`}>{renderRow(item, entityType, apiIndex, isLoading, openRemoveDialog)}<Divider /></Fragment>)}
                </div>
              </Stack>
            )
        }
      </TabPanel>,
      )}
    </TabContext>;

  return <>
    <DeleteEntityDialog
      handleClose={() => setDeleteState({ ...deleteState, open: false })}
      onSuccess={() => removeEntity(deleteState.item, deleteState.type)}
      open={deleteState.open}
      title={t('common:component.delete-entity-type-dialog.hint.remove', { entityType: deleteState.type, accountDisplay: account?.display })}
    >
      <Trans i18nKey="common:component.remove-entity-type-dialog.hint.delete-warning" values={{ entityType: deleteState.type, accountDisplay: account?.display }} />
    </DeleteEntityDialog>
    <AssignDialog<EntityTypeBase>
      apiUri={apiUri}
      apiFilters={{ shared: true, ...FILTER_OUT.deleted }}
      assignableText={{
        headerAction: t('common:common.action.assign').toLocaleLowerCase(),
        headerEntity: t(`common:terms.${dialogEntityType}`, { count: T_ONE }),
        headerEntities: t(`common:terms.${dialogEntityType}`, { count: T_ONE }),
        headerEntityToSelect: account?.display ?? '',
      }}
      assignedElement={<Icon icon="CheckCircleIcon" style={{ color: theme.palette.success.light }} />}
      assignNewEntityElement={(cardData) => (
        <LoadingButton
          sx={{ color: theme.palette.primary.main, fontWeight: 'bold' }}
          task={() => {
            return assignEntityType(cardData)
              .then(() => setIdsToCheck((prev) => [...prev, cardData._id]))
              .catch(({ message }: Error) => assignErrorToast(message));
          }}
          variant="text"
        >
          {t('common:common.action.assign')}
        </LoadingButton>
      )}
      handleClose={() => setAddShareDialog?.((prevState) => ({ ...prevState, open: false }))}
      idsToCheck={idsToCheck}
      itemHeader={(cardData) => {
        if (dialogEntityType === CacheDataTypes.INSTALLATION_TEMPLATE) {
          const { display, thingTypeId, deviceTypeId } = cardData as InstallationTemplate;
          return (
            <Grid container spacing={1} sx={{ flexWrap: 'wrap' }}>
              <Grid item xs={12} sm={12}>
                <ListPaperColumn
                  label={t('common:common.labels.display')}
                  width="100%"
                >
                  {display}
                </ListPaperColumn>
              </Grid>
              <Grid item xs={6} sm={6}>
                <ListPaperColumn
                  label={t('common:terms.thing-type', { count: T_ONE })}
                  width="100%"
                >
                  <FetchOneOfAll dataType={CacheDataTypes.THING_TYPE} id={thingTypeId} renderFactory={simpleRenderer} />
                </ListPaperColumn>
              </Grid>
              <Grid item xs={6} sm={6}>
                <ListPaperColumn
                  label={`${t('common:terms.device', { count: T_ONE })} ${t('common:common.labels.type')}`}
                  width="100%"
                >
                  <FetchOneOfAll dataType={CacheDataTypes.DEVICE_TYPE} id={deviceTypeId} renderFactory={({ deleted, manufacturer, model }: DeviceType) => <SimpleElement data={{ display: `${manufacturer} ${model}`, deleted }} />} />
                </ListPaperColumn>
              </Grid>
            </Grid>
          );
        }
        return (
          <ListPaperColumn
            label={t(`common:terms.${dialogEntityType}`, { count: T_ONE })}
            width="80%"
          >
            {cardData.display}
          </ListPaperColumn>
        );
      }}
      open={addShareDialog?.open}
    />

    <StyledCard data-testid={props?.['data-testid']}>
      <StyledCardHeader
        action={<>
          {data.map(({ entityType, entityKey }, i) => <Collapse key={`${entityType}-${i}`} in={index === i}>
            {buttonVisible && !disableAddShare &&
              (() => {
                const AddShareButton = styled(Button)({ float: 'right' });
                return (
                  <AddShareButton data-testid="add-share-button" onClick={() => {
                    onAddShare?.(entityType);
                    setAddShareDialog({ open: true, entityType });
                  }}>
                    {t('common:component.entity-management.action.add-shared-entity', { entity: t(entityKey, { count: T_MANY }) })}
                  </AddShareButton>
                );
              })()
            }
          </Collapse>)}
        </>}
        title={
          <ResponsiveText
            variant="h6"
            responsiveOptions={{
              minFontSize: '1rem',
              maxFontSize: '1.25rem',
              textLengthThreshold: 30,
            }}
          >
            {titleText}
          </ResponsiveText>
        }
      />
      <StyledCardContent>
        {renderSingleOrMultiple}
        {paginationComponent
          ? <Box display="flex" justifyContent="center" marginY={1}>
            {paginationComponent}
          </Box>
          : null
        }
      </StyledCardContent>
    </StyledCard>
  </>;
};
